import React, { useState, useEffect, useCallback } from 'react';
import {
  FileText, Send, Search, CheckCircle, Clock, Eye, Copy, ExternalLink, Download, Bell,
  Shield, ShieldCheck, Package, X, ChevronDown, History, AlertTriangle
} from 'lucide-react';
import {
  getSignatureRequests, sendForSignature, nudgeSignature,
  getEnvelopes, getEnvelope, createEnvelope, voidEnvelope,
  getEnvelopeAuditTrail, getEnvelopeCertificate, getSignatureAuditTrail
} from '../../services/adminDealFlowApi';
import api from '../../services/api';
import { Card, CardContent } from '../../components/new-components/ui/card';
import { Button } from '../../components/new-components/ui/button';
import { Badge } from '../../components/new-components/ui/badge';
import { Input } from '../../components/new-components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../../components/new-components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/new-components/ui/select';
import { toast } from 'sonner';

const RISK_TIERS = [
  { value: '1', label: 'Tier 1 — Low Risk', desc: 'Internal acknowledgments' },
  { value: '2', label: 'Tier 2 — Standard', desc: 'Contracts, agreements' },
  { value: '3', label: 'Tier 3 — High Risk', desc: 'Regulated documents, OTP required' },
];

const ESignatures: React.FC = () => {
  const [tab, setTab] = useState<'requests' | 'envelopes'>('requests');
  const [requests, setRequests] = useState<any[]>([]);
  const [documents, setDocuments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showSend, setShowSend] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendForm, setSendForm] = useState({
    tenantDocumentId: '', signerEmail: '', signerName: '', dealId: '', riskTier: '1'
  });
  const [search, setSearch] = useState('');
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Envelope state
  const [envelopes, setEnvelopes] = useState<any[]>([]);
  const [envelopeTotal, setEnvelopeTotal] = useState(0);
  const [showEnvelope, setShowEnvelope] = useState(false);
  const [selectedEnvelope, setSelectedEnvelope] = useState<any>(null);
  const [auditTrail, setAuditTrail] = useState<any[]>([]);
  const [showAuditTrail, setShowAuditTrail] = useState(false);
  const [auditTrailTitle, setAuditTrailTitle] = useState('');
  const [voidReason, setVoidReason] = useState('');
  const [showVoid, setShowVoid] = useState(false);
  const [voidingId, setVoidingId] = useState<string>('');

  // Multi-recipient envelope creation
  const [showCreateEnvelope, setShowCreateEnvelope] = useState(false);
  const [envelopeForm, setEnvelopeForm] = useState({
    title: '',
    riskTier: '1',
    routingMode: 'parallel',
    documentIds: [] as string[],
    recipients: [{ email: '', name: '', role: 'signer', signingOrder: 1 }],
  });
  const [creatingEnvelope, setCreatingEnvelope] = useState(false);

  // Schedule state
  const [scheduleMode, setScheduleMode] = useState(false);
  const [scheduleDate, setScheduleDate] = useState('');
  const [envelopeScheduleMode, setEnvelopeScheduleMode] = useState(false);
  const [envelopeScheduleDate, setEnvelopeScheduleDate] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [rRes, dRes, eRes]: any[] = await Promise.all([
        getSignatureRequests(),
        api.get('/admin/documents', { params: { limit: 200 } }),
        getEnvelopes({ limit: 50 }),
      ]);
      const rData = rRes?.data?.data || rRes?.data || [];
      setRequests(Array.isArray(rData) ? rData : []);
      const docPayload = dRes?.data || dRes;
      const docs = docPayload?.documents || [];
      setDocuments(docs);
      const eData = eRes?.data || eRes;
      setEnvelopes(eData?.envelopes || []);
      setEnvelopeTotal(eData?.total || 0);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSend = async () => {
    if (!sendForm.tenantDocumentId || !sendForm.signerEmail) return;
    if (scheduleMode && !scheduleDate) return;
    setSending(true);
    try {
      await sendForSignature({
        tenantDocumentId: sendForm.tenantDocumentId,
        signerEmail: sendForm.signerEmail,
        signerName: sendForm.signerName || undefined,
        dealId: sendForm.dealId || undefined,
        riskTier: parseInt(sendForm.riskTier) as any,
        scheduled_at: scheduleMode && scheduleDate ? new Date(scheduleDate).toISOString() : undefined,
      });
      toast.success(scheduleMode ? 'Signature request scheduled' : 'Document sent for signature');
      setShowSend(false);
      setSendForm({ tenantDocumentId: '', signerEmail: '', signerName: '', dealId: '', riskTier: '1' });
      setScheduleMode(false);
      setScheduleDate('');
      load();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to send');
    } finally { setSending(false); }
  };

  const handleCreateEnvelope = async () => {
    if (!envelopeForm.title || !envelopeForm.documentIds.length || !envelopeForm.recipients[0]?.email) return;
    if (envelopeScheduleMode && !envelopeScheduleDate) return;
    setCreatingEnvelope(true);
    try {
      await createEnvelope({
        title: envelopeForm.title,
        riskTier: parseInt(envelopeForm.riskTier),
        routingMode: envelopeForm.routingMode,
        documentIds: envelopeForm.documentIds,
        recipients: envelopeForm.recipients.filter(r => r.email),
        scheduledAt: envelopeScheduleMode && envelopeScheduleDate ? new Date(envelopeScheduleDate).toISOString() : undefined,
      });
      toast.success(envelopeScheduleMode ? 'Envelope scheduled' : 'Envelope created and sent');
      setShowCreateEnvelope(false);
      setEnvelopeForm({
        title: '', riskTier: '1', routingMode: 'parallel',
        documentIds: [], recipients: [{ email: '', name: '', role: 'signer', signingOrder: 1 }],
      });
      load();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to create envelope');
    } finally { setCreatingEnvelope(false); }
  };

  const openEnvelope = async (envId: string) => {
    try {
      const res: any = await getEnvelope(envId);
      setSelectedEnvelope(res?.data || res);
      setShowEnvelope(true);
    } catch { toast.error('Failed to load envelope'); }
  };

  const openAuditTrail = async (type: 'envelope' | 'signature', id: string, title: string) => {
    try {
      const res: any = type === 'envelope'
        ? await getEnvelopeAuditTrail(id)
        : await getSignatureAuditTrail(id);
      setAuditTrail(res?.data || []);
      setAuditTrailTitle(title);
      setShowAuditTrail(true);
    } catch { toast.error('Failed to load audit trail'); }
  };

  const handleVoid = async () => {
    if (!voidReason || !voidingId) return;
    try {
      await voidEnvelope(voidingId, voidReason);
      toast.success('Envelope voided');
      setShowVoid(false);
      setVoidReason('');
      setVoidingId('');
      load();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to void');
    }
  };

  const copySignLink = (sigId: string) => {
    const url = `${window.location.origin}/sign/${sigId}`;
    navigator.clipboard.writeText(url);
    setCopiedId(sigId);
    toast.success('Signing link copied');
    setTimeout(() => setCopiedId(null), 2000);
  };

  const filtered = requests.filter(r => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (r.template_name || '').toLowerCase().includes(s) ||
           (r.signer_email || '').toLowerCase().includes(s) ||
           (r.deal_title || '').toLowerCase().includes(s);
  });

  const statusBadge = (status: string) => {
    const map: Record<string, string> = {
      signed: 'bg-green-50 text-green-700',
      completed: 'bg-green-50 text-green-700',
      sent: 'bg-blue-50 text-blue-700',
      in_progress: 'bg-blue-50 text-blue-700',
      viewed: 'bg-amber-50 text-amber-700',
      pending: 'bg-gray-50 text-gray-600',
      draft: 'bg-gray-50 text-gray-600',
      declined: 'bg-red-50 text-red-700',
      voided: 'bg-red-50 text-red-700',
      expired: 'bg-gray-50 text-gray-500',
    };
    return map[status] || '';
  };

  const tierBadge = (tier: number) => {
    const map: Record<number, string> = {
      1: 'bg-blue-50 text-blue-600',
      2: 'bg-amber-50 text-amber-600',
      3: 'bg-red-50 text-red-600',
    };
    const labels: Record<number, string> = { 1: 'Tier 1', 2: 'Tier 2', 3: 'Tier 3' };
    return (
      <Badge variant="secondary" className={`text-xs ${map[tier] || ''}`}>
        <Shield className="h-3 w-3 mr-1" />{labels[tier] || 'N/A'}
      </Badge>
    );
  };

  const addRecipient = () => {
    setEnvelopeForm(f => ({
      ...f,
      recipients: [...f.recipients, { email: '', name: '', role: 'signer', signingOrder: f.recipients.length + 1 }],
    }));
  };

  const updateRecipient = (idx: number, field: string, value: any) => {
    setEnvelopeForm(f => ({
      ...f,
      recipients: f.recipients.map((r, i) => i === idx ? { ...r, [field]: value } : r),
    }));
  };

  const removeRecipient = (idx: number) => {
    if (envelopeForm.recipients.length <= 1) return;
    setEnvelopeForm(f => ({ ...f, recipients: f.recipients.filter((_, i) => i !== idx) }));
  };

  return (
    <div className="space-y-5 sm:space-y-6 min-w-0">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-display font-semibold text-[#242424]">E-Signatures</h1>
          <p className="text-sm text-muted-foreground mt-1">Send documents for signature and track compliance</p>
        </div>
        <div className="grid grid-cols-2 sm:flex gap-2 w-full sm:w-auto">
          <Button variant="outline" onClick={() => setShowCreateEnvelope(true)} className="justify-center">
            <Package className="h-4 w-4 mr-2" /> Create Envelope
          </Button>
          <Button onClick={() => setShowSend(true)} className="justify-center">
            <Send className="h-4 w-4 mr-2" /> Quick Send
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
        <Card className="border-border">
          <CardContent className="p-4">
            <p className="text-2xl font-bold text-[#242424]">{requests.length}</p>
            <p className="text-xs text-muted-foreground">Total Requests</p>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="p-4">
            <p className="text-2xl font-bold text-green-600">{requests.filter(r => r.status === 'signed').length}</p>
            <p className="text-xs text-muted-foreground">Signed</p>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="p-4">
            <p className="text-2xl font-bold text-blue-600">{requests.filter(r => r.status === 'sent').length}</p>
            <p className="text-xs text-muted-foreground">Awaiting</p>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="p-4">
            <p className="text-2xl font-bold text-amber-600">{requests.filter(r => r.status === 'viewed').length}</p>
            <p className="text-xs text-muted-foreground">Viewed</p>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="p-4">
            <p className="text-2xl font-bold text-purple-600">{envelopeTotal}</p>
            <p className="text-xs text-muted-foreground">Envelopes</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-border overflow-x-auto overscroll-x-contain">
        <button
          onClick={() => setTab('requests')}
          className={`shrink-0 px-4 py-2 text-sm font-medium border-b-2 transition ${
            tab === 'requests' ? 'border-[#242424] text-[#242424]' : 'border-transparent text-muted-foreground hover:text-[#242424]'
          }`}
        >
          Signature Requests
        </button>
        <button
          onClick={() => setTab('envelopes')}
          className={`shrink-0 px-4 py-2 text-sm font-medium border-b-2 transition ${
            tab === 'envelopes' ? 'border-[#242424] text-[#242424]' : 'border-transparent text-muted-foreground hover:text-[#242424]'
          }`}
        >
          Envelopes ({envelopeTotal})
        </button>
      </div>

      {tab === 'requests' && (
        <>
          {/* Search */}
          <div className="relative w-full sm:max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by document, signer, or deal..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>

          {/* Signature Requests Table */}
          <Card className="border-border">
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full min-w-[600px]">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left p-4 text-xs text-muted-foreground">Document</th>
                    <th className="text-left p-4 text-xs text-muted-foreground">Signer</th>
                    <th className="text-left p-4 text-xs text-muted-foreground">Risk</th>
                    <th className="text-left p-4 text-xs text-muted-foreground">Status</th>
                    <th className="text-left p-4 text-xs text-muted-foreground">Sent</th>
                    <th className="text-left p-4 text-xs text-muted-foreground">Signed</th>
                    <th className="text-left p-4 text-xs text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r: any) => (
                    <tr key={r.id} className="border-b border-border hover:bg-muted/50">
                      <td className="p-4 max-w-[220px]">
                        <div className="flex items-center gap-2 min-w-0">
                          <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                          <span className="text-sm text-[#242424] truncate">{r.template_name || 'Untitled'}</span>
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="text-sm text-[#242424]">{r.signer_name || '—'}</div>
                        <div className="text-xs text-muted-foreground">{r.signer_email || r.email || '—'}</div>
                      </td>
                      <td className="p-4">{tierBadge(r.risk_tier || 1)}</td>
                      <td className="p-4">
                        <Badge variant="secondary" className={`text-xs ${statusBadge(r.status)}`}>
                          {r.status === 'signed' && <CheckCircle className="h-3 w-3 mr-1" />}
                          {(r.status === 'sent' || r.status === 'pending') && <Clock className="h-3 w-3 mr-1" />}
                          {r.status === 'viewed' && <Eye className="h-3 w-3 mr-1" />}
                          {r.status}
                        </Badge>
                      </td>
                      <td className="p-4 text-sm text-muted-foreground">
                        {r.sent_at ? new Date(r.sent_at).toLocaleDateString() : '—'}
                      </td>
                      <td className="p-4 text-sm text-muted-foreground">
                        {r.completed_at ? new Date(r.completed_at).toLocaleDateString() : '—'}
                      </td>
                      <td className="p-4">
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openAuditTrail('signature', r.id, r.template_name || 'Signature')}
                            title="View audit trail"
                          >
                            <History className="h-4 w-4 text-gray-500" />
                          </Button>
                          {r.status !== 'signed' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={async () => {
                                try {
                                  await nudgeSignature(r.id);
                                  toast.success('Nudge email sent');
                                } catch { toast.error('Failed to send nudge'); }
                              }}
                              title="Send reminder email"
                            >
                              <Bell className="h-4 w-4 text-amber-600" />
                            </Button>
                          )}
                          {r.status === 'signed' && r.signed_copy_id && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={async () => {
                                try {
                                  const res: any = await api.get(`/admin/documents/${r.signed_copy_id}/download`);
                                  const url = res?.url || res?.data?.url;
                                  if (url) window.open(url, '_blank');
                                  else toast.error('Could not get download URL');
                                } catch { toast.error('Failed to download signed document'); }
                              }}
                              title="Download signed document"
                            >
                              <Download className="h-4 w-4 text-green-600" />
                            </Button>
                          )}
                          {r.status !== 'signed' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => copySignLink(r.id)}
                              title="Copy signing link"
                            >
                              {copiedId === r.id ? <CheckCircle className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => window.open(`/sign/${r.id}`, '_blank')}
                            title="Open signing page"
                          >
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filtered.length === 0 && !loading && (
                    <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">
                      {requests.length === 0 ? 'No signature requests yet.' : 'No matching requests'}
                    </td></tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </>
      )}

      {tab === 'envelopes' && (
        <Card className="border-border">
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full min-w-[600px]">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left p-4 text-xs text-muted-foreground">Envelope</th>
                  <th className="text-left p-4 text-xs text-muted-foreground">Risk</th>
                  <th className="text-left p-4 text-xs text-muted-foreground">Routing</th>
                  <th className="text-left p-4 text-xs text-muted-foreground">Status</th>
                  <th className="text-left p-4 text-xs text-muted-foreground">Progress</th>
                  <th className="text-left p-4 text-xs text-muted-foreground">Created</th>
                  <th className="text-left p-4 text-xs text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {envelopes.map((e: any) => (
                  <tr key={e.id} className="border-b border-border hover:bg-muted/50">
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <Package className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <span className="text-sm font-medium text-[#242424]">{e.title}</span>
                          {e.description && <p className="text-xs text-muted-foreground truncate max-w-[200px]">{e.description}</p>}
                        </div>
                      </div>
                    </td>
                    <td className="p-4">{tierBadge(e.risk_tier)}</td>
                    <td className="p-4">
                      <span className="text-xs capitalize text-muted-foreground">{e.routing_mode}</span>
                    </td>
                    <td className="p-4">
                      <Badge variant="secondary" className={`text-xs ${statusBadge(e.status)}`}>
                        {e.status}
                      </Badge>
                    </td>
                    <td className="p-4">
                      <span className="text-sm text-muted-foreground">
                        {e.signed_count || 0}/{e.recipient_count || 0}
                      </span>
                    </td>
                    <td className="p-4 text-sm text-muted-foreground">
                      {e.created_at ? new Date(e.created_at).toLocaleDateString() : '—'}
                    </td>
                    <td className="p-4">
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => openEnvelope(e.id)} title="View details">
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => openAuditTrail('envelope', e.id, e.title)} title="Audit trail">
                          <History className="h-4 w-4 text-gray-500" />
                        </Button>
                        {!['completed', 'voided', 'expired'].includes(e.status) && (
                          <Button size="sm" variant="ghost" onClick={() => { setVoidingId(e.id); setShowVoid(true); }} title="Void envelope">
                            <AlertTriangle className="h-4 w-4 text-red-500" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {envelopes.length === 0 && !loading && (
                  <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">
                    No envelopes yet. Click "Create Envelope" to get started.
                  </td></tr>
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Quick Send Dialog */}
      <Dialog open={showSend} onOpenChange={setShowSend}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle>Send Document for Signature</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Document</label>
              <Select value={sendForm.tenantDocumentId} onValueChange={v => setSendForm(p => ({ ...p, tenantDocumentId: v }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a document..." />
                </SelectTrigger>
                <SelectContent>
                  {documents.map((d: any) => (
                    <SelectItem key={d.id} value={d.id}>
                      {d.title || d.original_filename} ({d.folder})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Risk Tier</label>
              <Select value={sendForm.riskTier} onValueChange={v => setSendForm(p => ({ ...p, riskTier: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {RISK_TIERS.map(t => (
                    <SelectItem key={t.value} value={t.value}>
                      <div className="flex flex-col">
                        <span>{t.label}</span>
                        <span className="text-xs text-muted-foreground">{t.desc}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Signer Email</label>
              <Input
                type="email"
                value={sendForm.signerEmail}
                onChange={e => setSendForm(p => ({ ...p, signerEmail: e.target.value }))}
                placeholder="signer@example.com"
              />
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Signer Name (optional)</label>
              <Input
                value={sendForm.signerName}
                onChange={e => setSendForm(p => ({ ...p, signerName: e.target.value }))}
                placeholder="John Smith"
              />
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="scheduleQuickSend" checked={scheduleMode}
                onChange={(e) => { setScheduleMode(e.target.checked); if (!e.target.checked) setScheduleDate(''); }}
                className="rounded border-gray-300" />
              <label htmlFor="scheduleQuickSend" className="text-sm text-muted-foreground cursor-pointer flex items-center gap-1">
                <Clock className="h-3.5 w-3.5" /> Schedule for later
              </label>
            </div>
            {scheduleMode && (
              <Input type="datetime-local" value={scheduleDate} onChange={(e) => setScheduleDate(e.target.value)}
                min={new Date().toISOString().slice(0, 16)} />
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowSend(false); setScheduleMode(false); setScheduleDate(''); }}>Cancel</Button>
            <Button onClick={handleSend} disabled={!sendForm.tenantDocumentId || !sendForm.signerEmail || sending || (scheduleMode && !scheduleDate)}>
              {sending ? 'Sending...' : scheduleMode ? 'Schedule' : 'Send'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Envelope Dialog */}
      <Dialog open={showCreateEnvelope} onOpenChange={setShowCreateEnvelope}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Multi-Signer Envelope</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Envelope Title</label>
              <Input
                value={envelopeForm.title}
                onChange={e => setEnvelopeForm(f => ({ ...f, title: e.target.value }))}
                placeholder="e.g. Series A Subscription Agreement"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-muted-foreground mb-1 block">Risk Tier</label>
                <Select value={envelopeForm.riskTier} onValueChange={v => setEnvelopeForm(f => ({ ...f, riskTier: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {RISK_TIERS.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm text-muted-foreground mb-1 block">Routing</label>
                <Select value={envelopeForm.routingMode} onValueChange={v => setEnvelopeForm(f => ({ ...f, routingMode: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="parallel">Parallel (all at once)</SelectItem>
                    <SelectItem value="sequential">Sequential (in order)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Document</label>
              <Select
                value={envelopeForm.documentIds[0] || ''}
                onValueChange={v => setEnvelopeForm(f => ({ ...f, documentIds: [v] }))}
              >
                <SelectTrigger><SelectValue placeholder="Select a document..." /></SelectTrigger>
                <SelectContent>
                  {documents.map((d: any) => (
                    <SelectItem key={d.id} value={d.id}>
                      {d.title || d.original_filename} ({d.folder})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm text-muted-foreground">Recipients</label>
                <Button variant="ghost" size="sm" onClick={addRecipient}>+ Add Recipient</Button>
              </div>
              <div className="space-y-3">
                {envelopeForm.recipients.map((r, idx) => (
                  <div key={idx} className="flex gap-2 items-start border rounded-lg p-3 bg-muted/30">
                    <div className="flex-1 space-y-2">
                      <div className="grid grid-cols-2 gap-2">
                        <Input
                          placeholder="Email"
                          type="email"
                          value={r.email}
                          onChange={e => updateRecipient(idx, 'email', e.target.value)}
                        />
                        <Input
                          placeholder="Name (optional)"
                          value={r.name}
                          onChange={e => updateRecipient(idx, 'name', e.target.value)}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <Select value={r.role} onValueChange={v => updateRecipient(idx, 'role', v)}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="signer">Signer</SelectItem>
                            <SelectItem value="approver">Approver</SelectItem>
                            <SelectItem value="reviewer">Reviewer</SelectItem>
                            <SelectItem value="witness">Witness</SelectItem>
                            <SelectItem value="observer">Observer (CC)</SelectItem>
                          </SelectContent>
                        </Select>
                        {envelopeForm.routingMode === 'sequential' && (
                          <Input
                            type="number"
                            min={1}
                            placeholder="Order"
                            value={r.signingOrder}
                            onChange={e => updateRecipient(idx, 'signingOrder', parseInt(e.target.value) || 1)}
                          />
                        )}
                      </div>
                    </div>
                    {envelopeForm.recipients.length > 1 && (
                      <Button variant="ghost" size="sm" onClick={() => removeRecipient(idx)}>
                        <X className="h-4 w-4 text-red-400" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="scheduleEnvelope" checked={envelopeScheduleMode}
                onChange={(e) => { setEnvelopeScheduleMode(e.target.checked); if (!e.target.checked) setEnvelopeScheduleDate(''); }}
                className="rounded border-gray-300" />
              <label htmlFor="scheduleEnvelope" className="text-sm text-muted-foreground cursor-pointer flex items-center gap-1">
                <Clock className="h-3.5 w-3.5" /> Schedule for later
              </label>
            </div>
            {envelopeScheduleMode && (
              <Input type="datetime-local" value={envelopeScheduleDate} onChange={(e) => setEnvelopeScheduleDate(e.target.value)}
                min={new Date().toISOString().slice(0, 16)} />
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreateEnvelope(false); setEnvelopeScheduleMode(false); setEnvelopeScheduleDate(''); }}>Cancel</Button>
            <Button
              onClick={handleCreateEnvelope}
              disabled={!envelopeForm.title || !envelopeForm.documentIds.length || !envelopeForm.recipients[0]?.email || creatingEnvelope || (envelopeScheduleMode && !envelopeScheduleDate)}
            >
              {creatingEnvelope ? 'Creating...' : envelopeScheduleMode ? 'Schedule' : 'Create & Send'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Envelope Detail Dialog */}
      <Dialog open={showEnvelope} onOpenChange={setShowEnvelope}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              {selectedEnvelope?.title || 'Envelope'}
            </DialogTitle>
          </DialogHeader>
          {selectedEnvelope && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground">Status</p>
                  <Badge variant="secondary" className={`text-xs mt-1 ${statusBadge(selectedEnvelope.status)}`}>
                    {selectedEnvelope.status}
                  </Badge>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Risk Tier</p>
                  <div className="mt-1">{tierBadge(selectedEnvelope.risk_tier)}</div>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Routing</p>
                  <p className="text-sm capitalize mt-1">{selectedEnvelope.routing_mode}</p>
                </div>
              </div>
              {selectedEnvelope.source_hash && (
                <div>
                  <p className="text-xs text-muted-foreground">Source Document Hash</p>
                  <p className="text-xs font-mono text-gray-500 mt-1 break-all">{selectedEnvelope.source_hash}</p>
                </div>
              )}
              <div>
                <p className="text-sm font-medium mb-2">Recipients ({selectedEnvelope.recipients?.length || 0})</p>
                <div className="space-y-2">
                  {(selectedEnvelope.recipients || []).map((r: any) => (
                    <div key={r.id} className="flex items-center justify-between border rounded-lg p-3 bg-muted/30">
                      <div>
                        <p className="text-sm font-medium">{r.signer_name || r.signer_email}</p>
                        <p className="text-xs text-muted-foreground">{r.signer_email} — {r.recipient_role}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {r.otp_verified && <span title="OTP Verified"><ShieldCheck className="h-4 w-4 text-green-500" /></span>}
                        <Badge variant="secondary" className={`text-xs ${statusBadge(r.status)}`}>
                          {r.status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Audit Trail Dialog */}
      <Dialog open={showAuditTrail} onOpenChange={setShowAuditTrail}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              Audit Trail — {auditTrailTitle}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {auditTrail.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">No audit events recorded yet.</p>
            ) : (
              <div className="space-y-3">
                {auditTrail.map((e: any, i: number) => (
                  <div key={e.id || i} className="flex gap-3 items-start">
                    <div className="w-2 h-2 rounded-full bg-blue-400 mt-2 flex-shrink-0" />
                    <div className="flex-1 border-b border-border pb-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">{e.event_type}</span>
                        <span className="text-xs text-muted-foreground">
                          {new Date(e.created_at).toLocaleString()}
                        </span>
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {e.actor_email || e.actor_id || e.actor_type}
                        {e.ip_address && <span className="ml-2 font-mono">{e.ip_address}</span>}
                      </div>
                      {e.document_hash && (
                        <div className="text-xs font-mono text-gray-400 mt-1 truncate">
                          Hash: {e.document_hash}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Void Confirmation Dialog */}
      <Dialog open={showVoid} onOpenChange={setShowVoid}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" /> Void Envelope
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              Voiding an envelope will cancel all pending signatures. This action cannot be undone.
            </p>
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Reason for voiding *</label>
              <Input
                value={voidReason}
                onChange={e => setVoidReason(e.target.value)}
                placeholder="e.g. Terms changed, duplicate request"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowVoid(false); setVoidReason(''); }}>Cancel</Button>
            <Button variant="destructive" onClick={handleVoid} disabled={!voidReason}>Void Envelope</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ESignatures;
